import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
} from "../../interfaces/DecisionPriority";
import { IPiece, Position, PieceType } from "../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
} from "../../../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../../../utils/MCTSMoveGenerationService";

/**
 * Priority 7: King Safety Stay
 *
 * If our king is under DIRECT threat (an enemy piece can capture it next turn),
 * force the AI to move the king to safety by filtering out all non-king moves.
 * P6 (King Safety Move) already removed dangerous king moves, so the remaining
 * king moves in the pool are safe escape routes.
 *
 * If no direct threat exists, this priority does nothing (NO EFFECT).
 */
export class KingSafetyStayPriority extends BaseDecisionPriority {
  readonly priority = 7;
  readonly name = "King Safety Stay";
  readonly description =
    "Force king escape when under direct threat — filter all non-king moves";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const ourKing = this.findOurKing(context.gameState, context.gameState.isLightTurn);
    return !!ourKing;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const playerColor = isPlayerTurn ? "Light" : "Dark";

    // Find our king
    const ourKing = this.findOurKing(gameState, isPlayerTurn);
    if (!ourKing) {
      this.addLog(`❌ No king found for ${playerColor}`);
      return {
        shouldEarlyExit: false,
        reasoning: "No king found",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `👑 ${playerColor} King: ${ourKing.piece.type} at [${ourKing.position.row},${ourKing.position.col}]`
    );

    // Scan enemy attack vectors and get direct threats
    const attackers = this.scanEnemyAttackVectors(gameState, ourKing);

    if (attackers.length === 0) {
      this.addLog(`✅ No direct threats to king`);
      return {
        shouldEarlyExit: false,
        reasoning: "No direct threats to king",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // King IS under direct threat — force king-only moves
    this.addLog(`🚨 King under direct threat from ${attackers.length} attacker(s)!`);

    const kingMoves = context.allPossibleMoves.filter((moveInfo) => {
      const piece = MCTSUtils_v2.getPieceAt(gameState.board, moveInfo.fromPosition);
      return piece && piece.isKing;
    });

    const nonKingMoveCount = context.allPossibleMoves.length - kingMoves.length;
    this.addLog(`🛡️ Forcing king escape — filtered ${nonKingMoveCount} non-king moves, ${kingMoves.length} king moves remain`);

    if (kingMoves.length === 0) {
      // No king moves at all (P6 may have removed them all, or king has no moves)
      // Fallback: allow all moves to prevent crash
      this.addLog(`⚠️ No king moves available — allowing all moves as fallback`);
      return {
        shouldEarlyExit: false,
        reasoning: "King threatened but no escape moves available",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    return this.createFilterResult(
      kingMoves,
      `KING UNDER THREAT: Forcing king escape — ${attackers.join(', ')}`,
      {
        attackers: attackers.length,
        kingMoves: kingMoves.length,
        nonKingMovesFiltered: nonKingMoveCount,
        logs: this.analysisLogs,
      }
    );
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  /**
   * Scan all enemy attack vectors on our king.
   * Logs enemy pieces and direct threats. Returns list of attacker descriptions.
   */
  private scanEnemyAttackVectors(
    gameState: any,
    ourKing: { piece: IPiece; position: Position }
  ): string[] {
    this.addLog(`🔍 ENEMY ATTACK VECTOR SCAN on king at [${ourKing.position.row},${ourKing.position.col}]:`);

    // Generate enemy moves
    const testState = cloneFullGameState(gameState);
    testState.isLightTurn = !ourKing.piece.isLight;
    const enemyMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState);

    // Find attackers that can reach our king
    const attackers: string[] = [];
    for (const enemyMove of enemyMoves) {
      const attackerPiece = MCTSUtils_v2.getPieceAt(testState.board, enemyMove.fromPosition);
      if (!attackerPiece || attackerPiece.isLight === ourKing.piece.isLight) continue;

      if (enemyMove.move.position.row === ourKing.position.row &&
          enemyMove.move.position.col === ourKing.position.col) {
        // Skip probabilistic threats (Monkey vs Bear has 66% failure)
        if (attackerPiece.type === PieceType.Monkey && ourKing.piece.type === PieceType.Bear) {
          continue;
        }
        const desc = `${attackerPiece.type} at [${enemyMove.fromPosition.row},${enemyMove.fromPosition.col}]`;
        if (!attackers.includes(desc)) attackers.push(desc);
      }
    }

    // List nearby enemy pieces
    const nearbyPieces: string[] = [];
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isLight !== ourKing.piece.isLight) {
          const dist = Math.abs(row - ourKing.position.row) + Math.abs(col - ourKing.position.col);
          nearbyPieces.push(`${piece.type}${piece.isKing ? ' (KING)' : ''} at [${row},${col}] (dist: ${dist})`);
        }
      }
    }

    this.addLog(`   Enemy pieces on board: ${nearbyPieces.length}`);
    for (const p of nearbyPieces) this.addLog(`   • ${p}`);

    if (attackers.length > 0) {
      this.addLog(`   ⚠️ DIRECT THREATS to king: ${attackers.length}`);
      for (const a of attackers) this.addLog(`   ⚔️ ${a} CAN ATTACK KING`);
    } else {
      this.addLog(`   ✅ No enemy pieces can directly attack king right now`);
    }

    return attackers;
  }

  private findOurKing(
    gameState: any,
    isLightTurn: boolean
  ): { piece: IPiece; position: Position } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isKing && piece.isLight === isLightTurn) {
          return { piece, position: { row, col } };
        }
      }
    }
    return null;
  }
}
